// Third party
import {
  DataGridPro,
  GridApiPro,
  GridCellParams,
  GridColDef,
  GridFetchRowsParams,
  GridSortModel,
  useGridApiRef,
  GridActionsCellItemProps,
} from '@mui/x-data-grid-pro';
import Typography from '@mui/material/Typography';
import startCase from 'lodash/startCase';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';

// Hooks
import { useTranslation } from 'react-i18next';
import {
  Chip, useTheme,
} from '@mui/material';
import {
  MutableRefObject, ReactElement, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import Card from '@mui/material/Card';
import { GridFilterModel } from '@mui/x-data-grid/models/gridFilterModel';
import { unstable_debounce as debounce } from '@mui/utils';
import get from 'lodash/get';
import Stack from '@mui/material/Stack';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { useScreenResize } from '@mgm/common/hooks/useResize';
import { Label, LabelColor, BaseImage } from '@mgm/common/components/base';
import { CustomNoRowsOverlay } from '@mgm/common/components';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';
import { useInventoryFilters } from '../../hooks';
import { useGetProductsByLocationFirstPage } from '../../../../hooks/api/useInventory';
import { useTable } from '../../../../hooks/useTable';
import { useCommon } from '../../../../hooks/useCommon';

// Components
import { InventoryTableHeader } from './InventoryTableHeader';
import { MemoizedFilterTabs } from '../../../../components/base';
// Data
import { memberActions } from '../../../../store/team/slice';
import { commonActions } from '../../../../store/common/slice';
import { getInventory, getSelectedLocationId } from '../../../../store/inventory/selector';

// Types
import { getIsPrintMode } from '../../../../store/common/selectors';
import { PrintWrapper } from '../../../../layout/wrapper/PrintWrapper';
import { inventoryActions } from '../../../../store/inventory/slice';
import { useFetchRemainingProducts } from '../../Products/useFetchRemainingProducts';
import {
  TPageInfo, TTransferQuantity, TVariant,
} from '../../../../types';
import { TableActions } from '../../../../components/table/TableActions';
import { INDEX_TO_COLOR } from '../../../../data';

const InventoryTable = () => {
  const { t } = useTranslation(['inputs', 'common', 'team']);
  const selectedLocationId = useAppSelector(getSelectedLocationId);
  const inventoryItems = useAppSelector(getInventory);
  const apiRef = useGridApiRef() as MutableRefObject<GridApiPro>;
  const dispatch = useAppDispatch();
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);
  const { getInventoryByFilters } = useInventoryFilters();

  const { locationsByPermissions } = useCommon();
  const {
    localText, handleScrollTable, filterRows, sortRows,
  } = useTable(apiRef);
  const isPrintMode = useAppSelector(getIsPrintMode);
  const [rows, setRows] = useState<TVariant[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const componentRef = useRef<HTMLDivElement | null>(null);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({} as GridFilterModel);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterId, setFilterId] = useState<number>(Number(locationsByPermissions?.[1]?.id));
  const [isFullTable, setIsFullTable] = useState(false);

  const { data: pageInfo, isRefetching } = useGetProductsByLocationFirstPage();

  const { refetch } = useFetchRemainingProducts(pageInfo as TPageInfo);

  const fetchRemainingProductsWithWorker = useCallback(async () => {
    if (selectedLocationId !== null && selectedLocationId > 1 && pageInfo) {
      const { isLoading } = inventoryItems[selectedLocationId];
      if ('hasNextPage' in pageInfo && isLoading) {
        await refetch();
      }
    }
  }, [pageInfo]);

  const fetchRows = useCallback(
    async (params: GridFetchRowsParams) => {
      const totalRows = getInventoryByFilters.length;
      const firstRowToRender = params.firstRowToRender || 0;
      const lastRowToRender = Math.min(params.lastRowToRender || 10, totalRows);

      let filteredRows = [...getInventoryByFilters];

      const quickFilterValues = params.filterModel?.quickFilterValues || [];

      if (params.sortModel && params.sortModel.length > 0) {
        filteredRows = sortRows(filteredRows, params.sortModel);
      }

      if (quickFilterValues.length > 0) {
        filteredRows = getInventoryByFilters.filter((product) => {
          const productName = product.node?.displayName?.toLowerCase() || '';

          return quickFilterValues.every((value) => {
            const searchTerms = value?.toLowerCase().split(' ');
            return searchTerms.every((term: string) => productName.includes(term));
          });
        });
      }

      if (params.filterModel && params.filterModel?.items?.length > 0) {
        filteredRows = filterRows(filteredRows, params.filterModel);
      }

      const slice = filteredRows.slice(firstRowToRender, lastRowToRender);

      return { slice, total: filteredRows.length };
    },
    [getInventoryByFilters],
  );

  const handleFetchRows = useCallback(
    async (params: GridFetchRowsParams) => {
      const { slice, total } = await fetchRows(params);

      apiRef.current?.unstable_replaceRows(params.firstRowToRender, slice);
      setRowCount(total);
    },
    [apiRef, fetchRows],
  );

  const debouncedHandleFetchRows = useMemo(
    () => debounce(handleFetchRows, 200),
    [handleFetchRows],
  );

  useEffect(() => {
    if (getInventoryByFilters?.length === 0) {
      setRows([]);
      setRowCount(0);
      return;
    }

    const fetchInitialRows = async () => {
      const { slice, total } = await fetchRows({
        firstRowToRender: 0,
        lastRowToRender: 10,
        sortModel,
        filterModel,
      });
      setRows(slice);
      setRowCount(total);
    };

    fetchInitialRows();
  }, [fetchRows, getInventoryByFilters, sortModel, filterModel]);

  const getLengthByStatus = (locationId: number) => {
    if (!inventoryItems[locationId]) {
      return 0;
    }

    return inventoryItems[locationId].loadedInventoryCount;
  };

  const tabs = useMemo(() => {
    if (locationsByPermissions.length !== 0) {
      return locationsByPermissions.filter((location) => location.id > 1).map((location, index) => ({
        value: location.id,
        label: location.name,
        color: INDEX_TO_COLOR[index],
        count: getLengthByStatus(location.id),
      }));
    }
    return [];
  }, [locationsByPermissions]);

  const { pageHeight, resizableDiv, isDesktopSmallScreen } = useScreenResize(59);

  const onCellDoubleClick = (params: GridCellParams) => {
    dispatch(memberActions.setSelectedMember(params.row));

    setTimeout(() => {
      dispatch(commonActions.setIsOpenPagesNav(true));
    }, 100);
  };

  const handleFilterStatus = (newValue: number) => {
    dispatch(inventoryActions.setSelectedLocationId(Number(newValue)));
    setFilterId(newValue);
  };

  const renderCustomNoRowsOverlay = () => <CustomNoRowsOverlay text={t('itLooksLikeThereAreNoData', { ns: 'common' })} />;

  const inventoryColumns: Array<GridColDef> = [
    {
      field: 'productTitle',
      headerName: t('Title'),
      width: 300,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          {row.node.image?.url
            ? (
              <Stack direction="row" alignItems="center" spacing={2}>
                <BaseImage
                  disabledEffect
                  visibleByDefault
                  alt={row.node.displayName}
                  src={row.node.image?.url}
                  sx={{ borderRadius: 1.5, width: 44, height: 44 }}
                />

                <Typography variant="caption">
                  {startCase(row.node.displayName)}
                </Typography>
              </Stack>
            ) : (
              <Typography variant="caption">
                {startCase(row.node.displayName)}
              </Typography>
            )}
        </Box>
      ),
    },
    {
      field: 'productVendor',
      headerName: t('Product Vendor'),
      width: 200,
      renderCell: ({ row }) => (
        <Box alignItems="center" display="flex" height="100%">
          <Typography variant="caption">
            {startCase(row.node.product.vendor)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'categoryName',
      headerName: t('Category Name'),
      width: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%" pt={1}>
          <Typography variant="caption">
            {startCase(row.node?.product?.category?.name) || '/'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'productType',
      headerName: t('Product Type'),
      width: 200,
      renderCell: ({ row }) => (
        <Box p={1} display="flex" alignItems="center" height="100%">
          <Typography variant="caption">
            {startCase(row.node.product.productType)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'Tags',
      headerName: t('Tags'),
      width: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          {row.node.product.tags?.length ? (
            row.node.product.tags.map((tag: string, index: number) => (
              <Chip key={`${tag}-${index.toString()}`} label={tag} size="small" />
            ))) : (
              <Typography variant="caption">
                /
              </Typography>
          )}

        </Box>
      ),
    },
    {
      field: 'Variant name',
      headerName: t('Variant name'),
      width: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography fontWeight={600} variant="caption">
            {startCase(row.node.title)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'availableInventoryQuantity',
      headerName: t('Available'),
      width: 200,
      renderCell: ({ row }) => {
        const quantity = get(row, 'node.inventoryItem.inventoryLevel.quantities[0].quantity', "Doesn't exist in this location");

        let labelColor;
        if (quantity === "Doesn't exist in this location") {
          labelColor = 'error';
        } else if (quantity < 5) {
          labelColor = 'error';
        } else if (quantity >= 5 && quantity <= 10) {
          labelColor = 'warning';
        } else {
          labelColor = 'success';
        }

        return (
          <Box display="flex" alignItems="center" height="100%">
            <Label variant="soft" color={labelColor as LabelColor}>
              {quantity}
            </Label>
          </Box>
        );
      },
    },
    {
      field: 'Incoming Inventory Quantity',
      headerName: t('Incoming'),
      width: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%" pt={1}>
          <Typography variant="caption">
            {startCase(row.node.inventoryItem.inventoryLevel ? row.node.inventoryItem.inventoryLevel?.quantities[1].quantity : "Doesn't exist in this location")}
          </Typography>
        </Box>
      ),
    }, {
      field: 'Commited Inventory Quantity',
      headerName: t('Commited'),
      width: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%" pt={1}>
          <Typography variant="caption">
            {startCase(row.node.inventoryItem.inventoryLevel ? row.node.inventoryItem.inventoryLevel?.quantities[2].quantity : "Doesn't exist in this location")}
          </Typography>
        </Box>
      ),
    }, {
      field: 'Damaged Inventory Quantity',
      headerName: t('Damaged'),
      width: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%" pt={1}>
          <Typography variant="caption">
            {startCase(row.node.inventoryItem.inventoryLevel ? row.node.inventoryItem.inventoryLevel?.quantities[3].quantity : "Doesn't exist in this location")}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'On hand Inventory Quantity',
      headerName: t('On hand'),
      width: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="baseLicenterne" height="100%" pt={1}>
          <Typography variant="caption">
            {startCase(row.node.inventoryItem.inventoryLevel ? row.node.inventoryItem.inventoryLevel?.quantities[4].quantity : "Doesn't exist in this location")}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'Quality control Inventory Quantity',
      headerName: t('Quality control'),
      width: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%" pt={1}>
          <Typography variant="caption">
            {startCase(row.node.inventoryItem.inventoryLevel ? row.node.inventoryItem.inventoryLevel?.quantities[5].quantity : "Doesn't exist in this location")}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'Reserved Inventory Quantity',
      headerName: t('Reserved'),
      width: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%" pt={1}>
          <Typography variant="caption">
            {startCase(row.node.inventoryItem.inventoryLevel ? row.node.inventoryItem.inventoryLevel?.quantities[6].quantity : "Doesn't exist in this location")}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'Safety stocked Inventory Quantity',
      headerName: t('Safety Stock'),
      width: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%" pt={1}>
          <Typography variant="caption">
            {startCase(row.node.inventoryItem.inventoryLevel ? row.node.inventoryItem.inventoryLevel?.quantities[7].quantity : "Doesn't exist in this location")}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('actions', { ns: 'common' }),
      disableExport: true,
      sortable: false,
      align: 'right',
      getActions: ({ row }) => {
        const openTeamNav = () => {
          const transferVariant : TTransferQuantity = {
            selectetVariant: row,
            targetLocation: null,
            quantity: 0,
            selectedQuantityType: null,
          };
          dispatch(inventoryActions.setSelectedVariant(transferVariant));

          setTimeout(() => {
            dispatch(inventoryActions.setIsTransferWindowOpen(true));
          }, 100);
        };

        return TableActions({
          onConfirm: row.node.displayName === 'Custom Variant' ? undefined : openTeamNav,
        }) as ReactElement<GridActionsCellItemProps>[];
      },
    },
  ];

  useEffect(() => {
    fetchRemainingProductsWithWorker();
  }, [selectedLocationId]);

  const renderTableHeader = useCallback(() => (
    <InventoryTableHeader
      handleScrollTable={handleScrollTable}
      printRef={componentRef}
      apiRef={apiRef}
      isFullTable={isFullTable}
      setIsFullTable={setIsFullTable}
    />
  ), [apiRef, isFullTable]);

  return (
    <PrintWrapper componentRef={componentRef}>
      <div ref={resizableDiv}>
        <Card sx={{
          // eslint-disable-next-line no-nested-ternary
          height: isDesktopSmallScreen ? 'auto' : isFullTable && rows?.length > 0 ? 'auto' : pageHeight !== 0 && !isPrintMode ? pageHeight : 'auto',
          pb: 6,
          ...(isMobile && {
            height: 'auto',
            background: 'unset',
            boxShadow: 'unset',
            px: 0,
            pt: 0,
            pb: '30px',
          }),
        }}
        >
          <MemoizedFilterTabs tabs={tabs} onFilterChange={handleFilterStatus} initialFilter={filterId} />
          <DataGridPro
            apiRef={apiRef}
            rows={rows}
            localeText={localText}
            rowCount={rowCount}
            slots={{
              toolbar: renderTableHeader as any,
              loadingOverlay: LinearProgress as any,
              noRowsOverlay: renderCustomNoRowsOverlay,
            }}
            loading={isRefetching}
            sx={{
              px: 0,
              '.MuiDataGrid-main > *:first-of-type': {
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
              },
            }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                printOptions: {
                  hideFooter: true,
                  hideToolbar: true,
                },
              },
            }}
            {...(!isMobile && {
              initialState: { pinnedColumns: { left: ['productTitle'], right: ['actions'] } },
            })}
            getRowId={(row) => row.node.id}
            columns={inventoryColumns}
            hideFooterPagination
            disableRowSelectionOnClick
            sortingMode="server"
            filterMode="server"
            rowsLoadingMode="server"
            hideFooter
            onCellDoubleClick={onCellDoubleClick}
            checkboxSelection={false}
            onFetchRows={debouncedHandleFetchRows}
            onFilterModelChange={setFilterModel}
            showToolbar
            onSortModelChange={setSortModel}
            className={isPrintMode ? 'print-mode' : ''}
          />
        </Card>
      </div>
    </PrintWrapper>
  );
};

export {
  InventoryTable,
};
